/**
 * Pure placement math for an anchored popover — no DOM, no framework (reusable by a React port).
 * Needs no knowledge of the popover's own height: a `below` panel is anchored by its top edge, an
 * `above` panel by its bottom edge — so one synchronous pass at open time positions it correctly,
 * with no measure-then-reposition round trip.
 */

export interface AnchorRect {
	top: number;
	left: number;
	bottom: number;
	right: number;
	width: number;
	height: number;
}

export interface PopoverViewport {
	width: number;
	height: number;
}

export type PopoverSide = 'below' | 'above';

export interface AnchoredPlacementInput {
	/** The trigger element's viewport rect (e.g. `getBoundingClientRect()`). */
	anchor: AnchorRect;
	/** The viewport size (e.g. `{ width: innerWidth, height: innerHeight }`). */
	viewport: PopoverViewport;
	/** Desired popover width in px. */
	width: number;
	/** Gap between the anchor edge and the popover (px). */
	gap?: number;
	/** Minimum distance kept from every viewport edge (px). */
	margin?: number;
}

export interface AnchoredPlacement {
	side: PopoverSide;
	/** Viewport-space left, for `position: fixed`. */
	left: number;
	/** Height cap; pair with `overflow: auto` on the body so it scrolls past this. */
	maxHeight: number;
	/** Viewport-space top (set for `side === 'below'`). */
	top?: number;
	/** Viewport-space bottom offset (set for `side === 'above'`). */
	bottom?: number;
}

const DEFAULT_GAP = 6;
const DEFAULT_MARGIN = 8;

function clamp(value: number, min: number, max: number): number {
	if (max < min) {
		return min;
	}
	return Math.min(Math.max(value, min), max);
}

export function computeAnchoredPlacement(input: AnchoredPlacementInput): AnchoredPlacement {
	const gap = input.gap ?? DEFAULT_GAP;
	const margin = input.margin ?? DEFAULT_MARGIN;
	const { anchor, viewport, width } = input;

	const spaceBelow = viewport.height - anchor.bottom - gap - margin;
	const spaceAbove = anchor.top - gap - margin;

	// Right-align to the anchor's right edge, then clamp within the viewport.
	const left = clamp(anchor.right - width, margin, viewport.width - width - margin);

	if (spaceBelow >= spaceAbove) {
		return { side: 'below', left, top: anchor.bottom + gap, maxHeight: Math.max(0, spaceBelow) };
	}
	return { side: 'above', left, bottom: viewport.height - (anchor.top - gap), maxHeight: Math.max(0, spaceAbove) };
}
